import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import Seo from "@/components/Seo";
import { PageHeader } from "@/components/PageHeader";
import { Input } from "@/components/ui/input";
import { checkSetupToken, completeSetup } from "@/lib/api";

const TOKEN_KEY = "dh_admin_token";

const fieldClass =
  "w-full border border-white/10 bg-charcoal-900/80 px-4 py-3 text-sm font-light text-sand placeholder:text-mist/50 focus:border-gold/60 focus:outline-none focus:ring-1 focus:ring-gold/40 transition-colors duration-300";

export default function SetupAdmin() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const token = params.get("token") || "";

  const [state, setState] = useState("checking");
  const [email, setEmail] = useState("");
  const [invalidReason, setInvalidReason] = useState(
    "This setup link is invalid, has already been used, or has expired."
  );
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [error, setError] = useState("");
  const [sending, setSending] = useState(false);

  useEffect(() => {
    if (!token) {
      setState("invalid");
      return;
    }
    checkSetupToken(token)
      .then((res) => {
        setEmail(res.data.email);
        setState("valid");
      })
      .catch((err) => {
        const d = err.response?.data?.detail;
        if (typeof d === "string") setInvalidReason(d);
        setState("invalid");
      });
  }, [token]);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (password.length < 10) {
      setError("Password must be at least 10 characters.");
      return;
    }
    if (password !== confirm) {
      setError("Passwords do not match.");
      return;
    }
    setSending(true);
    try {
      const { data } = await completeSetup(token, password);
      localStorage.setItem(TOKEN_KEY, data.token);
      toast.success("Administrator password created", {
        description: "The setup link has been used and is now invalid.",
      });
      navigate("/inquiries");
    } catch (err) {
      const d = err.response?.data?.detail;
      setError(typeof d === "string" ? d : "Setup failed. Please request a new setup link.");
      setSending(false);
    }
  };

  return (
    <>
      <Seo title="Administrator Setup — Derrick Hall" siteName="Derrick Hall" description="One-time administrator password setup." />
      <PageHeader
        testId="setup-admin-header"
        eyebrow="Administration"
        title="Create Your Password"
        description="This one-time link sets the administrator password for this website. Once used, the link stops working."
      />

      <div className="mx-auto max-w-md px-6 pb-28" data-testid="setup-admin-page">
        {state === "checking" && (
          <p className="text-sm text-mist" data-testid="setup-checking">Checking your setup link…</p>
        )}

        {state === "invalid" && (
          <div className="border border-white/[0.08] bg-charcoal-900/60 p-8 sm:p-10" data-testid="setup-invalid">
            <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-gold">Link unavailable</p>
            <p className="mt-4 text-sm font-light leading-relaxed text-mist">{invalidReason}</p>
          </div>
        )}

        {state === "valid" && (
          <form onSubmit={submit} className="border border-white/[0.08] bg-charcoal-900/60 p-8 sm:p-10" data-testid="setup-form">
            <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-gold">One-time setup</p>
            <p className="mt-4 text-sm font-light text-mist">
              Setting the administrator password for{" "}
              <span className="text-sand-bright" data-testid="setup-email">{email}</span>
            </p>
            <div className="mt-8 space-y-5">
              <div>
                <label htmlFor="setup-password" className="mb-2 block font-mono text-[10px] uppercase tracking-[0.25em] text-mist">
                  New password
                </label>
                <Input
                  id="setup-password"
                  type="password"
                  data-testid="setup-input-password"
                  required
                  minLength={10}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className={fieldClass}
                  placeholder="At least 10 characters"
                  autoComplete="new-password"
                />
              </div>
              <div>
                <label htmlFor="setup-confirm" className="mb-2 block font-mono text-[10px] uppercase tracking-[0.25em] text-mist">
                  Confirm password
                </label>
                <Input
                  id="setup-confirm"
                  type="password"
                  data-testid="setup-input-confirm"
                  required
                  minLength={10}
                  value={confirm}
                  onChange={(e) => setConfirm(e.target.value)}
                  className={fieldClass}
                  placeholder="Repeat the password"
                  autoComplete="new-password"
                />
              </div>
              <p className="text-xs font-light leading-relaxed text-mist/70">
                Use a unique password for this website — not your CCCHAPS password or any
                password you use elsewhere.
              </p>
              {error && (
                <p data-testid="setup-error" className="border border-red-900/50 bg-red-950/40 px-4 py-3 text-sm font-light text-red-300">
                  {error}
                </p>
              )}
              <button
                type="submit"
                data-testid="setup-submit-btn"
                disabled={sending}
                className="w-full bg-gold px-8 py-4 font-mono text-[11px] uppercase tracking-[0.2em] text-charcoal-950 transition-colors duration-300 hover:bg-gold-light disabled:cursor-not-allowed disabled:opacity-50"
              >
                {sending ? "Setting password…" : "Set Password & Sign In"}
              </button>
            </div>
          </form>
        )}
      </div>
    </>
  );
}
